import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown } from 'lucide-react';

/** Readable width for portaled entity pickers (project name + code + badge). */
const PORTAL_MENU_WIDTH_PX = 360;

/**
 * Searchable combobox with click-outside close, keyboard navigation,
 * and custom rendering.
 *
 * Variants:
 *   default  → standard select height (uses select-admin / select classes)
 *   form     → rounded-full form field (44px / h-11, matches FormSelect).
 *              Menu is always portaled (modal-safe) — same contract as FormSelect.
 *   pill     → rounded-full (44px / h-11, matches PillSelect visually)
 *
 * Use variant="pill" when the combobox sits alongside PillSelect filters
 * in a toolbar/filter panel — ensures consistent height and style.
 *
 * Portaled menus (form variant, or when menuMinWidth is set):
 *   Rendered via createPortal + fixed positioning so `.modal-content`
 *   overflow and sticky `.modal-footer` / `.modal-header` cannot clip the
 *   list. z-index 10002 matches DatePickerField (above modal chrome at 10001).
 *   - form (no menuMinWidth): panel width matches the trigger (FormSelect).
 *   - menuMinWidth truthy: fixed 360px panel (narrow table-cell escape hatch).
 *
 * Async search:
 *   Pass onSearch to enable server-side search. When provided, client-side
 *   filtering is bypassed — items are assumed pre-filtered by the caller.
 *   The component debounces calls internally (debounceMs, default 300).
 *   Use displayValue to show the selected label when the item isn't in
 *   the current results list.
 *
 * Creatable:
 *   Pass creatableLabel + onCreateNew to show a "create new" footer option
 *   in the dropdown (e.g. for "Add custom institution" use cases).
 */
const SearchableCombobox = ({
  items = [],
  value,
  onChange,
  placeholder = 'Select...',
  clearLabel,
  searchPlaceholder = 'Search...',
  emptyLabel = 'No results found',
  displayFn = (item) => String(item),
  searchFn = (item, term) => displayFn(item).toLowerCase().includes(term),
  valueFn = (item) => item?.id ?? '',
  renderItem,
  disabled = false,
  loading = false,
  loadingLabel = 'Loading...',
  className = '',
  variant = 'default',
  theme = 'employee',
  icon: Icon,
  iconColor = 'text-gray-500',
  minWidth = 'min-w-[160px]',
  menuMinWidth = '',
  onSearch,
  debounceMs = 300,
  creatableLabel,
  onCreateNew,
  displayValue,
  autoOpen = false,
}) => {
  const isAdmin = theme === 'admin';
  const isFormVariant = variant === 'form';
  // Form/modal contexts must portal (FormSelect + DatePickerField contract).
  // menuMinWidth keeps the narrow-column 360px escape hatch for table cells.
  const usePortal = isFormVariant || Boolean(menuMinWidth);
  const useFixedPortalWidth = Boolean(menuMinWidth);
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [term, setTerm] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const ref = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const updateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownMaxHeight = 288; // max-h-72
    const spaceBelow = viewportHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUpward = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

    // Explicit width — never leave width:auto with w-full children (that
    // stretches the panel to nearly the full viewport under fixed layout).
    const width = useFixedPortalWidth
      ? Math.min(PORTAL_MENU_WIDTH_PX, viewportWidth - 16)
      : Math.min(rect.width, viewportWidth - 16);
    const left = Math.max(8, Math.min(rect.left, viewportWidth - width - 8));

    setDropdownStyle({
      position: 'fixed',
      left,
      width,
      // Above sticky modal header/footer (10001) and overlay (9999) — same as DatePickerField
      zIndex: 10002,
      ...(openUpward
        ? { bottom: viewportHeight - rect.top + 4, maxHeight: Math.min(spaceAbove, dropdownMaxHeight) }
        : { top: rect.bottom + 4, maxHeight: Math.min(spaceBelow, dropdownMaxHeight) }),
    });
  }, [useFixedPortalWidth]);

  // Click-outside only while open; account for the portaled menu node
  // (same pattern as FormSelect).
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (usePortal && menuRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, usePortal]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
    if (!isOpen) { setTerm(''); setHighlightIdx(-1); }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !usePortal) return;
    updateDropdownPosition();
    const update = () => updateDropdownPosition();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, usePortal, updateDropdownPosition]);

  const isAsync = typeof onSearch === 'function';

  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  const stableOnSearch = useCallback(
    (t) => { if (onSearchRef.current) onSearchRef.current(t); },
    [],
  );

  useEffect(() => {
    if (!isAsync || !isOpen) return;
    const id = setTimeout(() => stableOnSearch(term), debounceMs);
    return () => clearTimeout(id);
  }, [term, isAsync, isOpen, debounceMs, stableOnSearch]);

  const filtered = useMemo(() => {
    if (isAsync) return items;
    if (!term.trim()) return items;
    const t = term.toLowerCase();
    return items.filter((item) => searchFn(item, t));
  }, [items, term, searchFn, isAsync]);

  useEffect(() => {
    setHighlightIdx(-1);
  }, [filtered]);

  const select = useCallback((itemValue) => {
    onChange(itemValue);
    setIsOpen(false);
    setTerm('');
  }, [onChange]);

  const hasCreatable = !!creatableLabel && !!onCreateNew;

  const handleKeyDown = (e) => {
    const hasClear = !!clearLabel;
    const totalItems = filtered.length + (hasClear ? 1 : 0) + (hasCreatable ? 1 : 0);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((prev) => (prev + 1) % totalItems);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((prev) => (prev - 1 + totalItems) % totalItems);
    } else if (e.key === 'Enter' && highlightIdx >= 0) {
      e.preventDefault();
      const creatableIdx = (hasClear ? 1 : 0) + filtered.length;
      if (hasClear && highlightIdx === 0) {
        select('');
      } else if (hasCreatable && highlightIdx === creatableIdx) {
        onCreateNew(term);
        setIsOpen(false);
      } else {
        const idx = hasClear ? highlightIdx - 1 : highlightIdx;
        if (filtered[idx]) select(valueFn(filtered[idx]));
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const el = listRef.current.children[highlightIdx];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  // Match the selected item type-tolerantly: ids frequently round-trip
  // through strings (e.g. a prepopulated work-site id of 167 vs the list's
  // numeric 167), and strict === would silently drop the selection. Null /
  // undefined keep strict semantics so an empty value never matches an item.
  const valuesEqual = (a, b) => {
    if (a == null || b == null) return a === b;
    return String(a) === String(b);
  };
  const selected = items.find((item) => valuesEqual(valueFn(item), value));
  const display = loading && !isOpen
    ? loadingLabel
    : displayValue
    ? displayValue
    : selected
    ? displayFn(selected)
    : placeholder;

  const isPill = variant === 'pill';

  const focusRing = isAdmin
    ? 'focus:ring-red-500 focus:border-red-500'
    : 'focus:ring-blue-500 focus:border-blue-500';

  const triggerClasses = isPill
    ? `flex items-center gap-1.5 bg-white border border-gray-200 h-11 px-4 rounded-full shadow-sm hover:border-gray-300 transition-colors ${minWidth} focus:outline-none focus:ring-2 ${focusRing} text-sm font-medium ${!value ? 'text-gray-400' : 'text-gray-900'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`
    : isFormVariant
    ? `w-full text-left h-11 px-4 text-sm border border-gray-300 rounded-full cursor-pointer focus:outline-none focus:ring-2 ${
        isAdmin ? 'focus:ring-red-500 focus:border-red-500' : 'focus:ring-blue-500 focus:border-blue-800'
      } bg-white flex items-center justify-between ${
        !value ? 'text-gray-400' : 'text-gray-900'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`
    : `${isAdmin ? 'select-admin' : 'select'} w-full text-left pr-10 cursor-pointer ${
        !value ? 'text-gray-400' : 'text-gray-900'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`;

  const isFullWidth = className.includes('w-full');

  // FormSelect pattern: select on mousedown + preventDefault so the
  // document click-outside handler cannot unmount the menu before click.
  const selectOnMouseDown = (itemValue) => (e) => {
    e.preventDefault();
    select(itemValue);
  };

  const menuBody = (
    <>
      <div className="p-2 border-b border-gray-100 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            className={`w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${isAdmin ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
            placeholder={searchPlaceholder}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
      </div>
      <div
        className={usePortal ? 'overflow-y-auto min-h-0 flex-1' : 'overflow-y-auto max-h-52'}
        ref={listRef}
      >
        {clearLabel && (
          <button
            type="button"
            onMouseDown={selectOnMouseDown('')}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
              !value
                ? isAdmin ? 'bg-red-50 text-red-700 font-medium' : 'bg-blue-50 text-blue-700 font-medium'
                : 'text-gray-700'
            } ${highlightIdx === 0 ? 'bg-gray-100' : ''}`}
          >
            {clearLabel}
          </button>
        )}
        {loading && isAsync ? (
          <div className="px-3 py-4 text-sm text-gray-400 text-center">
            {loadingLabel}
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-3 py-4 text-sm text-gray-400 text-center">
            {emptyLabel}
          </div>
        ) : (
          filtered.map((item, idx) => {
            const itemValue = valueFn(item);
            const isSelected = valuesEqual(value, itemValue);
            const adjustedIdx = clearLabel ? idx + 1 : idx;
            const isHighlighted = highlightIdx === adjustedIdx;
            return renderItem ? (
              renderItem({
                item,
                itemValue,
                isSelected,
                isHighlighted,
                onSelect: () => select(itemValue),
                onMouseDownSelect: selectOnMouseDown(itemValue),
              })
            ) : (
              <button
                key={itemValue}
                type="button"
                onMouseDown={selectOnMouseDown(itemValue)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                  isSelected
                    ? isAdmin ? 'bg-red-50 text-red-700 font-medium' : 'bg-blue-50 text-blue-700 font-medium'
                    : 'text-gray-700'
                } ${isHighlighted ? 'bg-gray-100' : ''}`}
              >
                {displayFn(item)}
              </button>
            );
          })
        )}
        {hasCreatable && (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              onCreateNew(term);
              setIsOpen(false);
            }}
            className={`w-full text-left px-3 py-2 text-sm border-t border-gray-100 font-medium ${
              isAdmin ? 'text-red-600 hover:bg-red-50' : 'text-blue-800 hover:bg-blue-50'
            } ${highlightIdx === (clearLabel ? 1 : 0) + filtered.length ? 'bg-gray-100' : ''}`}
          >
            {typeof creatableLabel === 'function' ? creatableLabel(term) : creatableLabel}
          </button>
        )}
      </div>
    </>
  );

  const inlineMenu = isOpen && !usePortal && (
    <div className="absolute left-0 z-[9999] mt-1 min-w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-72 overflow-hidden">
      {menuBody}
    </div>
  );

  const portaledMenu = isOpen && usePortal && typeof document !== 'undefined' && createPortal(
    <div
      ref={menuRef}
      style={dropdownStyle}
      className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden flex flex-col"
    >
      {menuBody}
    </div>,
    document.body,
  );

  return (
    <div className={`relative ${isFullWidth && isPill ? 'w-full' : ''}`} ref={ref}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        className={triggerClasses}
      >
        {isPill && Icon && <Icon className={`w-4 h-4 ${iconColor} flex-shrink-0`} />}
        <span className={`truncate ${isPill ? 'flex-1 text-left' : ''}`}>{display}</span>
        {isPill && (
          <ChevronDown className={`w-3.5 h-3.5 text-gray-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
        {isFormVariant && (
          <svg className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isOpen ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
          </svg>
        )}
      </button>
      {inlineMenu}
      {portaledMenu}
    </div>
  );
};

export default SearchableCombobox;
